import { stockUnit, unitLabel } from './units';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const TOKEN_KEY = 'agrobridge-admin-token';

export function getToken() {
    try {
        return localStorage.getItem(TOKEN_KEY);
    } catch {
        return null;
    }
}

export function setToken(token) {
    try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    } catch {
        // storage unavailable
    }
}

export async function api(path, { method = 'GET', body, auth = true } = {}) {
    const headers = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const token = auth ? getToken() : null;
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`${API_URL}/api${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401) {
        setToken(null);
        if (typeof window !== 'undefined' && window.location.pathname !== '/') {
            window.location.href = '/';
        }
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.error || `Request failed (${res.status})`);
        err.status = res.status;
        throw err;
    }
    return data;
}

// Upload a File/Blob to the portal's own upload route (a photo attached to a
// support reply), returning the public image URL to store on the message.
export async function uploadImage(file) {
    const form = new FormData();
    form.append('image', file);
    const res = await fetch(`${API_URL}/api/admin/uploads`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Upload failed');
    return data.url;
}

// ---------- Formatting helpers ----------

export function money(value) {
    const n = Number(value) || 0;
    return `K${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function cap(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function shortDate(iso) {
    if (!iso) return '--';
    const d = new Date(iso);
    return `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })}, ${d.getFullYear()}`;
}

function longDate(iso) {
    if (!iso) return '--';
    const d = new Date(iso);
    const day = d.getDate();
    const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    return `${day}${suffix} ${d.toLocaleString('en-US', { month: 'long' })}, ${d.getFullYear()}`;
}

const ROLE_LABELS = { buyer: 'Buyer', seller: 'Seller', both: 'Buyer + Seller' };

// ---------- Mappers: API → admin table shapes ----------

export function mapUser(u) {
    return {
        _id: u.id,
        id: `#${String(u.id).slice(-6).toUpperCase()}`,
        name: u.name || 'Unnamed',
        farm: u.businessName || '',
        phone: u.phone,
        // Only set for accounts that added email + password sign-in.
        email: u.email || '--',
        role: ROLE_LABELS[u.role] || 'Buyer',
        points: u.points ?? 0,
        status: u.status === 'suspended' ? 'Inactive' : 'Active',
        location: u.location || '--',
        listings: String(u.listings ?? 0).padStart(2, '0'),
        orders: String(u.orders ?? 0).padStart(2, '0'),
        joined: longDate(u.createdAt),
    };
}

function priceLabel(listing) {
    const unit = listing.unit ? `/${listing.unit}` : '';
    return `${money(listing.retailPrice)}${unit}`;
}

export function mapListing(l) {
    const seller = l.seller || {};
    const pricing = ['Retail'];
    if (l.bulkPrice != null) pricing.push(`Bulk (${l.bulkMin ?? 0} +)`);
    return {
        _id: l.id,
        id: l.id,
        name: l.name,
        image: l.image || '/basket.png',
        category: cap(l.category),
        seller: (seller.businessName || seller.name || '').toUpperCase() || 'UNKNOWN',
        price: priceLabel(l),
        stock: l.stockKg ?? 0,
        // The seller's own unit, and the label that goes after the stock count.
        // `stockKg` is a legacy field name — it holds a count of `unit`, which
        // is only kilos for the sellers who list in kilos.
        unit: l.unit || '',
        stockUnit: stockUnit(l.unit),
        // Active | Pending | Declined | Removed | Expired. The API reports
        // "expired" as soon as the date passes, without waiting for its sweep.
        status: cap(l.status),
        pricing,
        totalSales: l.sales ?? 0,
        rating: l.rating ?? 0,
        sellerPoints: seller.points ?? 0,
        hasPendingChanges: Boolean(l.hasPendingChanges),
        pendingChanges: l.pendingChanges || null,
        // When the listing's shelf life runs out. Absent until an admin has
        // approved it — the clock starts at approval, not at submission.
        expiresAt: l.expiresAt || null,
        // How the seller hands the goods over. Listings predating these fields
        // offered both, which is also the schema default.
        pickup: l.pickup !== false,
        pickupInstructions: l.pickupInstructions || '',
        delivery: l.delivery !== false,
        deliveryFee: l.deliveryFee ?? null,
        // Raw values for the admin edit form — the fields above are formatted
        // for display ("K120 /bag", "Produce").
        description: l.description || '',
        categoryKey: l.category || 'produce',
        retailPrice: l.retailPrice ?? null,
        bulkPrice: l.bulkPrice ?? null,
        bulkMin: l.bulkMin ?? null,
    };
}

// Human-readable list of a listing's staged edits, e.g. "Name: Premium Tomatoes".
const CHANGE_LABELS = {
    name: 'Name',
    description: 'Description',
    image: 'Image',
    category: 'Category',
    unit: 'Unit',
    // Filled in from the listing's unit by describeChanges — see below.
    stockKg: 'Stock',
    retailPrice: 'Retail price',
    bulkPrice: 'Bulk price',
    bulkMin: 'Bulk minimum',
    location: 'Location',
    pickup: 'Pickup offered',
    pickupInstructions: 'Pickup instructions',
    delivery: 'Delivery offered',
    deliveryFee: 'Delivery fee',
};

// What the seller charges to deliver, worded the way the app words it for
// buyers. Three states that don't collapse: no quote (agreed with the buyer),
// an explicit 0 (free), and a real amount.
export function deliveryFeeLabel(fee) {
    if (fee == null || fee === '') return 'Agreed with buyer';
    const amount = Number(fee);
    if (Number.isNaN(amount)) return 'Agreed with buyer';
    return amount === 0 ? 'Free' : money(amount);
}

// A staged edit is reviewed as prose, so raw JSON values don't belong in it:
// a boolean reads as Yes/No, and a cleared delivery fee as what it means to a
// buyer rather than as "null".
function changeValue(key, value) {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (key === 'deliveryFee') return deliveryFeeLabel(value);
    if (value === '' || value == null) return '--';
    return String(value);
}

// `unit` is the listing's current unit, so a staged stock change is labelled in
// what it actually counts. An edit that changes the unit *and* the stock states
// the new stock in the new unit, which is what the seller typed it in.
export function describeChanges(pendingChanges, unit) {
    if (!pendingChanges) return [];
    const countedIn = unitLabel(pendingChanges.unit || unit);
    return Object.entries(pendingChanges).map(([key, value]) => ({
        key,
        label: key === 'stockKg' ? `Stock (${countedIn})` : CHANGE_LABELS[key] || key,
        value: key === 'image' ? value : changeValue(key, value),
    }));
}

const COMPLETED_BY = {
    seller: 'Seller',
    admin: 'Admin',
    auto: 'Automatically (left at ready)',
};

const PAYMENT_LABELS = {
    unpaid: 'Off the platform',
    paid: 'Paid in app',
    refund_due: 'Paid in app · refund due',
    refunded: 'Paid in app · refunded',
};

const PAYOUT_STATUS_LABELS = {
    held: 'payout held for the buyer',
    disputed: 'buyer disputed',
    due: 'payout due',
    paid: 'paid out',
    cancelled: 'refunded, not charged',
};

// Commission is taken from the seller's points wallet, paid in the app or not.
// Only paid orders completed while it was skimmed carry kwacha on the payout.
function paymentCommission(o) {
    const status = o.payout?.status;
    if (o.payout?.commission > 0 && status && status !== 'none') {
        return `${money(o.payout.commission)} from payout (${PAYOUT_STATUS_LABELS[status] || status})`;
    }
    return o.commissionPoints ? `${o.commissionPoints} points` : 'None';
}

// The Payouts table's commission cell.
export function commissionLabel(row) {
    if (row.payout?.commission > 0) return money(row.payout.commission);
    return row.commissionPoints ? `${row.commissionPoints} pts` : '--';
}

// One row on the Payouts page: a paid order whose money is owed to the seller
// (payout) or back to the buyer (refund).
export function mapMoneyOrder(o) {
    const buyer = o.buyer || {};
    const seller = o.seller || {};
    const payment = o.payment || {};
    return {
        _id: o.id,
        code: `#${o.code}`,
        buyer: buyer.name || 'Buyer',
        buyerPhone: buyer.phone || '',
        seller: seller.businessName || seller.name || 'Seller',
        sellerPhone: seller.phone || '',
        total: o.total,
        payout: o.payout || {},
        commissionPoints: o.commissionPoints || 0,
        refund: o.refund || {},
        paymentStatus: o.paymentStatus,
        // Where a refund goes by default: the wallet that paid.
        paidFrom: payment.msisdn || '',
        provider: payment.provider === 'mtn' ? 'MTN MoMo' : cap(payment.provider || ''),
        paymentRef: payment.reference || '',
        providerTxId: payment.providerTransactionId || '',
        completedAt: o.completedAt,
        receivedConfirmedAt: o.receivedConfirmedAt,
        dispute: o.dispute || {},
        cancelledAt: o.cancelledAt,
        cancelReason: o.cancelReason || '',
    };
}

export function mapAdminOrder(o) {
    const buyer = o.buyer || {};
    const seller = o.seller || {};
    const itemNames = (o.items || []).map((i) => i.name);
    const itemLabel = itemNames.length <= 1 ? itemNames[0] || '--' : `${itemNames[0]} + ${itemNames.length - 1}`;
    return {
        _id: o.id,
        id: `#${o.code}`,
        buyer: buyer.name || 'Buyer',
        item: itemLabel,
        fulfillment: cap(o.fulfilment),
        total: money(o.total),
        totalToPay: money(o.total),
        // The seller's own delivery quote, frozen at checkout. Never part of
        // `total` — AgroBridge doesn't collect it — so it is reported
        // separately. Null on pickup, and on a delivery with no quote agreed.
        deliveryFee: o.deliveryFee ?? null,
        status: cap(o.status),
        seller: seller.businessName || seller.name || '--',
        orderDate: longDate(o.receivedAt || o.createdAt),
        // Who completed it and what it cost the seller — the two facts a
        // commission dispute turns on. "Automatically" means the seller left
        // the order at ready and the API's stale-order sweep completed it.
        completedBy: COMPLETED_BY[o.completedBy] || '--',
        commission: paymentCommission(o),
        // How the goods were paid for — in the app at checkout, or off it.
        payment: PAYMENT_LABELS[o.paymentStatus] || PAYMENT_LABELS.unpaid,
        items: (o.items || []).map((i) => ({
            name: i.name,
            detail: i.unit ? `${money(i.price)}/per ${i.unit}` : money(i.price),
            price: money(i.price),
            image: i.image || '/basket.png',
        })),
    };
}

export function mapPackage(p) {
    return {
        _id: p.id,
        id: p.id,
        name: p.name,
        price: money(p.price),
        points: p.points,
        purchases: String(p.purchases ?? 0),
        date: shortDate(p.createdAt),
        recommended: Boolean(p.recommended),
        custom: Boolean(p.custom),
    };
}

const METHOD_LABELS = { 'mobile-money': 'Mobile money', card: 'Card', none: '--' };

export function mapPurchase(tx) {
    const user = tx.user || {};
    return {
        id: tx.id,
        seller: (user.businessName || user.name || '').toUpperCase() || 'UNKNOWN',
        package: tx.package?.name || 'Custom',
        paid: money(tx.amountPaid),
        points: tx.points,
        method: METHOD_LABELS[tx.method] || '--',
        date: shortDate(tx.createdAt),
    };
}

const REWARD_LABELS = { 'percent-off': '% off points package price', 'bonus-points': 'Bonus points' };

export function mapCoupon(c) {
    return {
        _id: c.id,
        id: c.id,
        code: c.code,
        description: c.description || '',
        rewardType: REWARD_LABELS[c.rewardType] || c.rewardType,
        rewardTypeRaw: c.rewardType,
        value: c.value,
        used: c.used ?? 0,
        maxRedemption: c.maxRedemption ?? 0,
        status: cap(c.status),
        expiry: c.expiry ? c.expiry.slice(0, 10) : '',
    };
}

// Platform settings — the two rationed giveaways. `remaining` is null when the
// pool is uncapped, which is deliberately distinct from 0 (exhausted), so the
// UI can tell "unlimited" apart from "none left".
export function mapSettings(s = {}) {
    const bonus = s.signupBonus || {};
    const coupons = s.coupons || {};
    return {
        signupBonus: {
            enabled: Boolean(bonus.enabled),
            points: bonus.points ?? 0,
            maxRecipients: bonus.maxRecipients ?? 0,
            awarded: bonus.awarded ?? 0,
            remaining: bonus.remaining ?? null,
            available: Boolean(bonus.available),
        },
        coupons: {
            maxTotalRedemptions: coupons.maxTotalRedemptions ?? 0,
            totalRedemptions: coupons.totalRedemptions ?? 0,
            remaining: coupons.remaining ?? null,
        },
    };
}

const AUDIENCE_LABELS = { all: 'Everyone', buyers: 'Buyers', sellers: 'Sellers' };

// An admin broadcast. Delivery runs behind the request that started it, so a
// row can be read back mid-send: `recipients`/`pushed` are what has gone out so
// far, not a final total, until `status` leaves "Sending".
export function mapPromotion(p) {
    return {
        _id: p.id,
        id: p.id,
        title: p.title,
        body: p.body || '',
        audience: AUDIENCE_LABELS[p.audience] || p.audience,
        recipients: p.recipients ?? 0,
        pushed: p.pushed ?? 0,
        pushFailed: p.pushFailed ?? 0,
        status: cap(p.status),
        // "Sending" is in-progress and "Failed" is a partial send — neither is
        // the green of a finished one.
        statusVariant: p.status === 'sent' ? 'Active' : p.status === 'failed' ? 'Inactive' : 'muted',
        error: p.error || '',
        sentBy: p.sentByName || '',
        sentAt: shortDate(p.createdAt),
    };
}

export function mapConversation(c) {
    const user = c.user || {};
    const last = c.messages?.[c.messages.length - 1];
    return {
        _id: c.id,
        id: c.id,
        name: user.name || 'User',
        role: ROLE_LABELS[user.role] || 'Buyer',
        farm: (user.businessName || '').toUpperCase(),
        // The server's flag: this thread is still waiting on a reply. Whether
        // the admin has READ it is a separate, per-browser fact — see
        // lib/supportSeen.js.
        waiting: Boolean(c.unreadForAdmin),
        // When it last said something, for comparing against what was read.
        lastAt: last?.at || null,
        // A photo-only message has no text to preview.
        preview: last ? `${last.text.slice(0, 40) || 'Photo'}...` : 'No messages yet',
        messages: (c.messages || []).map((m, i) => ({
            id: m._id || i,
            from: m.from === 'admin' ? 'admin' : 'them',
            text: m.text,
            // A photo of whatever is being reported. It can carry a caption, so
            // text and image are not exclusive.
            image: m.image || null,
            time: m.at ? new Date(m.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '',
        })),
    };
}
